import * as React from "react";
import {ReadAndWriteContext} from "../../../providers/ReadAndWriteProvider";
import {Button, capitalizeFirstLetter, delay} from "@vebox/etui-core";
import {updateDeviceSlaveSettings} from "../../data/controllers/ReadAndWriteController";
import {EditConfigurationTableTable} from "../../widgets/EditConfigurationTable";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import "./styles.scss"
import {EditableSetting, SettingToUpdate} from "../../data/models/UpdatingSetting";
import {VeBoxModal} from "../../../components/modal/VeBoxModal";
import {Display} from "../../../components/Display";
import {getEditingSettings, parseValue} from "../../ReadAndWrite/utils";
import {PAGES} from "../MainContentPage";
import {Loader} from "../../../components/Utils/loader/Loader";

interface EditConfigurationPageProps {

}

export const EditConfigurationPage: React.FC<EditConfigurationPageProps> = () => {
    const {
        deviceSlaveSettings,
        deviceGuid,
        availableRegisters,
        AppTranslations,
        changePage
    } = React.useContext(ReadAndWriteContext);

    const Texts = AppTranslations.readAndWrite;

    const deviceSettings = deviceSlaveSettings?.data?.settings?.settings ?? [];
    const deviceSlaveAvailableRegisters = availableRegisters?.data ?? [];
    const settings = getEditingSettings(deviceSettings, deviceSlaveAvailableRegisters);

    const [settingsToUpdate, setSettingsToUpdate] = React.useState<SettingToUpdate[]>([]);
    const [disabled, setDisabled] = React.useState<boolean>(true);
    const [showInfoModal, setShowInfoModal] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [errorTitle, setErrorTitle] = React.useState<string>("");


    const updateSettings = (register: EditableSetting, add = true, value?: string) => {
        const newValue: SettingToUpdate = {
            reg: register.reg,
            name: register.name
        };
        if (value) {
            newValue.newValue = value
        }
        const values = settingsToUpdate.filter((value) => value.reg !== newValue.reg);
        if (add) {
            values.push(newValue)
        }
        setSettingsToUpdate(values);
    }


    const handleOnTextInputChange = (register: EditableSetting) => {
        return (event: React.ChangeEvent<HTMLInputElement>) => {

            if (event.target.value === "") {
                updateSettings(register, false);
            } else {
                updateSettings(register, true, event.target.value);
            }
        }
    }

    const validateInput = (register: EditableSetting) => {
        return (event: React.ChangeEvent<HTMLInputElement>) => {
            const isValueEmpty = event?.target?.value === "" || event?.target?.value === undefined || event?.target?.value === null;
            const parsedValue = parseValue(event.target.value)
            if (!showInfoModal && !isValueEmpty) {
                if (isNaN(parsedValue)) {
                    setErrorMessage(Texts.dataFormatErrorNumericValue);
                    setErrorTitle(Texts.dataFormatErrorTitle);
                    setShowInfoModal(true);
                } else if (parsedValue < Number(register.min) || parsedValue > Number(register.max)) {
                    const translatedMessage = Texts.dataFormatErrorMinMax
                        .replace("{0}", register.min)
                        .replace("{1}", register.max);
                    setErrorMessage(translatedMessage);
                    setErrorTitle(Texts.dataFormatErrorTitle);
                    setShowInfoModal(true);
                } else {
                    setErrorTitle("");
                    setErrorMessage("");
                }
            }
        }
    }

    const handleOnCheckboxChange = (register: EditableSetting) => {
        return (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.checked) {
                updateSettings(register, true);
            } else {
                updateSettings(register, false);
            }
        }
    }

    React.useEffect(() => {
        if (errorMessage !== "") {
            setDisabled(true);
        } else {
            setDisabled(false);
        }
    }, [errorMessage])

    const submit = () => {
        setIsLoading(true);
        const settingsToUpdateList: SettingToUpdate[] = settingsToUpdate.map((setting) => {
            return {
                reg: setting.reg,
                newValue: setting?.newValue,
                name: setting.name
            }
        })
        updateDeviceSlaveSettings(deviceGuid, settingsToUpdateList)
            .then(() => {
                return deviceSlaveSettings.mutate()
            })
            .then(() => {
                return delay(500)
            })
            .then(() => {
                changePage(PAGES.UPDATING);
            })
            .catch((error) => {
                console.error(error);
                setIsLoading(false);
                setErrorTitle(AppTranslations.errorMessages.unexpectedError);
                setErrorMessage(AppTranslations.errorMessages.somethingWentWrong);
                setShowInfoModal(true);
            });

    }

    const goBack = () => {
        changePage(PAGES.SETTINGS);
    }


    const closeModal = () => {
        setShowInfoModal(false);
    }

    return (
        <>
            <Display isVisible={showInfoModal}>
                <div className={"cover-content-container"} style={{zIndex: "500"}}>
                    <VeBoxModal>
                        <VeBoxModal.Header title={errorTitle}/>
                        <VeBoxModal.Body style={{minHeight: "150px", minWidth: "435px"}}>
                            <VeBoxModal.Section>
                                <p className={"align-left"}>{errorMessage}</p>
                            </VeBoxModal.Section>
                        </VeBoxModal.Body>
                        <VeBoxModal.InfoAction
                            onCancel={closeModal}
                        />
                    </VeBoxModal>
                </div>
            </Display>
            <Display isVisible={!isLoading}>
                <div className={"settings-main-container-edit"}>
                    <EditConfigurationTableTable
                        editableSetting={settings}
                        handleOnTextInputChange={handleOnTextInputChange}
                        handleOnCheckboxChange={handleOnCheckboxChange}
                        validateInput={validateInput}
                        settingsToUpdate={settingsToUpdate}
                        isLoading={deviceSlaveSettings.isLoading || availableRegisters.isLoading}
                    />
                </div>
                <VeBoxCard className={"rw-card submit-button-edit"}>
                    <VeBoxCard.Body className={"rw-card-body align-right"}>
                        <Button onClick={goBack} variant={"tertiary"}>{capitalizeFirstLetter(
                            AppTranslations.genericWords.cancel)}</Button>
                        <Button
                            onClick={submit}
                            disabled={disabled}
                            variant={"strong"}
                        >{AppTranslations.genericWords.submit}</Button>
                    </VeBoxCard.Body>
                </VeBoxCard>
            </Display>
            <Display isVisible={isLoading}>
                <div className={"loader-container"}>
                    <Loader relativelyCentered={true}/>
                </div>
            </Display>
        </>
    );
};